import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  redirect,
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input } from "../../components/inputs/Input";
import { FormLayout } from "../../layout/FormLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { ImageLibrary } from "../../types/item";
import {GET_IMAGE_LIBRARY} from "./queries";
import {UPDATE_IMAGE_LIBRARY} from "./mutations";
import { usePermissions } from "../auth/api";
import { userStore } from "../../store/user";
import CustomSelect from "../../components/customSelect";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { useState } from "react";
import DeleteModal from "../../components/DeleteModal";
import {SecondaryButton, TertiaryButton} from "../../components/Button";
import CompactThumbUploader from "../../components/CompactThumbUploader";


const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationStore } = organizationStore;

export const ImageLibraryEditLoader = async ({ params }: LoaderFunctionArgs) => {
  const { id } = params;
  const [
    { data: imageData },
  ] = await Promise.all([
    client.query({
      query: GET_IMAGE_LIBRARY,
      variables: { id },
    }),
  ]);

  return {
    image: imageData.image_library_by_pk,
  };
};

export const ImageLibraryEditAction = async ({
  params,
  request,
}: ActionFunctionArgs) => {
  const user = getState().user;

  const { id } = params;
  const body = await request.formData();
  let bodyData: any = Object.fromEntries(body);
  let input;
  input = {
    ...bodyData,
    last_updated_by: user?.id,
  };

  try {
    await client.mutate({
      mutation: UPDATE_IMAGE_LIBRARY,
      variables: {
        input,
        where: {
          id: {
            _eq: id,
          },
        },
      },
    });

    toast.success("Image Library Item Updated");
    return redirect(`/image-library/list`);
  } catch (error) {
    toast.error("Failed to Update Image Library Item" + error);
  }
};

export const ImageLibraryEdit = () => {
  const organizations = useOrganizationStore((state) => state.organizations);
  const {image} = useLoaderData() as {image: ImageLibrary};
  const { deletePermission } = usePermissions("image_library");
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const handleClone = () => {
    navigate(`/image-library/${image.id}/clone`);
  };
  const user = getState().user;
  const organizationId = getOrganizationStore().organizationId;
  const admin = user?.role?.name === "admin"
  const [thumbBase64, setThumbBase64] = useState<string>(image.image_base64);

  const handleThumbReady = async (blob: Blob) => {
    const dataUrl = await blobToDataURL(blob);
    console.log("Data URL" + dataUrl)
    const rawBase64 = dataUrlToRawBase64(dataUrl);
    console.log("Base64" + rawBase64)
    setThumbBase64(rawBase64);
  };

  async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    })
  }

  function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
  }

  function handleClearImage() {
    setThumbBase64("");
  }

  return (
    <Form
      method="put"
      action={`/image-library/${image.id}/edit`}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    >
        <div className="flex">
          <div className="ml-auto py-2">
            <TertiaryButton onClick={handleClone}>
              Clone Image Library Item
            </TertiaryButton>
            {deletePermission && (
            <DeleteModal
              onConfirm={() => {
                fetcher.submit(
                  {},
                  {
                    method: "delete",
                    action: `/image-library/${image.id}/delete`,
                  }
                );
              }}
            >
              {({ setOpen }) => (
                <SecondaryButton onClick={() => setOpen(true)}>
                  Delete
                </SecondaryButton>
              )}
            </DeleteModal>
            )}
          </div>
        </div>
      <FormLayout>
        <div className="col-span-3">
          <Input
              label="Display Name"
              name="display_name"
              defaultValue={image.display_name}
          />
        </div>
        <div className="col-span-1">
          <IndeterminateCheckbox
              label="Is Active?"
              name="is_active"
              defaultChecked={image.is_active}
          />
        </div>
        {admin && (
            <div className="col-span-6">
              <CustomSelect
                  title="Organization"
                  name="organization_id"
                  required
                  defaultValue={image.organization_id}
                  options={organizations.map((org) => ({
                    label: org.name,
                    value: org.id,
                  }))}
              />
            </div>
        )}
        <div className="col-span-6">
          <Input
              label="Caption"
              name="caption"
              defaultValue={image.caption}
          />
        </div>
        <div className="col-span-6">
          <h3 className="text-lg font-semibold m-0">Upload Image
            <CompactThumbUploader onReady={handleThumbReady} allowLibraryAdd={false}/></h3>
        </div>
        <div className="col-span-6">
          {thumbBase64 && (
              <div className="relative w-[125px] h-[125px]">
                <img
                    src={`data:image/webp;base64,${thumbBase64}`}
                    width="100%"
                    height="100%"
                    alt="thumb"
                    className="rounded border border-slate-200 object-cover"
                />
                <button
                    type="button"
                    onClick={handleClearImage}
                    aria-label="Remove image"
                    title="Remove image"
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white
                            flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f04345]"
                >
                  X
                </button>
              </div>
          )}
        </div>
        {!admin && (
            <input
                type="hidden"
                value={organizationId || 0}
                name="organization_id"
            />
        )}
        <input
            type="hidden"
            value={thumbBase64}
            name="image_base64"/>
      </FormLayout>
    </Form>
  );
};
